/**
 * Badge — THE one small label (DESIGN-CALM-01 · Wave 1, Seth 2026-09-18:
 * "these CRMs look more modern and cleaner"). Pegasus had a dozen hand-rolled
 * pills — purple source tags, uppercase micro-labels, tinted counts — and
 * every row wore two or three. The rule now:
 *
 *   · neutral by default (slate on slate) — most labels carry no color
 *   · color ONLY for state: good · warn · bad · info — and one colored badge
 *     per row or card at most
 *   · plus = the PLUS membership gold, the one brand exception
 *   · never a badge for where a record came from (ESC / Pegasus)
 *
 * Dispatch STATUS keeps <StatusBadge> (lib/status.ts) and arrival WINDOWS keep
 * <WindowBadge> (lib/window.ts) — Seth 2026-09-18: the board's window / type
 * colors are the dispatcher's quick identifiers and stay exactly as they are.
 */
import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "@/lib/cn";

export type BadgeTone = "neutral" | "good" | "warn" | "bad" | "info" | "plus";

const TONES: Record<BadgeTone, string> = {
  neutral: "bg-slate-100 text-slate-600",
  good: "bg-emerald-50 text-emerald-700",
  warn: "bg-amber-50 text-amber-700",
  bad: "bg-rose-50 text-rose-700",
  info: "bg-brand-50 text-brand-700",
  plus: "bg-amber-50 text-amber-800",
};

export function Badge({
  tone = "neutral",
  size = "sm",
  icon,
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLSpanElement> & {
  tone?: BadgeTone;
  /** xs = inside dense rows; sm = the default. Both stay at or above 12px. */
  size?: "xs" | "sm";
  icon?: ReactNode;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1 whitespace-nowrap rounded-full font-semibold leading-tight",
        size === "xs" ? "px-1.5 py-px text-[12px]" : "px-2 py-0.5 text-[12px]",
        TONES[tone],
        className,
      )}
      {...rest}
    >
      {icon}
      <span className="truncate">{children}</span>
    </span>
  );
}
